import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, LuxuryButton, StatusBadge, EmptyState } from "@/components/lux";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, MessageSquare, Plus } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const CATLABEL = { deposit: "Deposit", withdrawal: "Withdrawal", fund: "Fund Regarding", other: "Other" };
const CATS = Object.entries(CATLABEL);

export default function Support() {
  const { data, loading, reload } = useFetch("/user/support/tickets");
  const [f, setF] = useState({ subject: "", message: "", category: "deposit" });
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");

  const tickets = data?.tickets || [];

  const createTicket = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data: res } = await api.post("/user/support/tickets", f);
      toast.success("Ticket created"); setF({ subject: "", message: "", category: "deposit" }); reload(); setOpenId(res.ticket_id);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReply = async (id) => {
    if (!reply.trim()) return;
    try {
      await api.post(`/user/support/tickets/${id}/reply`, { message: reply });
      setReply(""); reload();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageTitle title="Support" subtitle="Raise a ticket and chat with our support team" />
      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><Plus size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">New Ticket</h3></div>
          <form onSubmit={createTicket} className="space-y-3">
            <select className={inp} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="ticket-category">
              {CATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input className={inp} placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject" required />
            <textarea className={inp} rows={5} placeholder="Describe your issue..." value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="ticket-message" required />
            <LuxuryButton type="submit" className="w-full" disabled={busy} data-testid="ticket-submit">Submit Ticket</LuxuryButton>
          </form>
          <p className="mt-3 text-xs text-[var(--text-dim)]">Our team typically replies within 24 hours. You'll get a notification when we respond.</p>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><MessageSquare size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">My Tickets</h3></div>
          {loading ? <div className="skeleton h-40" /> : tickets.length ? (
            <div className="max-h-[520px] space-y-3 overflow-y-auto">
              {tickets.map((t) => (
                <div key={t.ticket_id} className="rounded-xl glass-2 p-4" data-testid={`ticket-${t.ticket_id}`}>
                  <button onClick={() => setOpenId(openId === t.ticket_id ? null : t.ticket_id)} className="flex w-full items-center justify-between text-left" data-testid={`ticket-toggle-${t.ticket_id}`}>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.subject}</p>
                      <p className="text-xs text-[var(--text-dim)]">{t.ticket_id} · <span className="text-[var(--gold)]">{CATLABEL[t.category] || "Other"}</span> · {(t.updated_at || "").slice(0, 10)}</p>
                    </div>
                    <StatusBadge status={t.status} />
                  </button>
                  {openId === t.ticket_id && (
                    <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
                      {t.messages.map((m, i) => (
                        <div key={i} className={`rounded-lg p-2.5 text-xs ${m.sender === "admin" ? "border border-[var(--gold)]/20 bg-[var(--gold)]/10" : "bg-white/5"}`}>
                          <p className="mb-1 font-semibold text-white">{m.sender === "admin" ? "Support" : "You"} <span className="font-normal text-[var(--text-dim)]">· {(m.created_at || "").slice(0, 16).replace("T", " ")}</span></p>
                          <p className="whitespace-pre-wrap text-[var(--text-dim)]">{m.body}</p>
                        </div>
                      ))}
                      {t.status !== "closed" && (
                        <div className="flex gap-2 pt-1">
                          <input className={inp + " flex-1"} placeholder="Type a reply..." value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`ticket-reply-input-${t.ticket_id}`} />
                          <button onClick={() => sendReply(t.ticket_id)} className="grid place-items-center rounded-xl btn3d-gold px-3 text-black" data-testid={`ticket-reply-send-${t.ticket_id}`}><Send size={16} /></button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : <EmptyState icon={LifeBuoy} title="No tickets yet" hint="Create a ticket to get help from our team." />}
        </GlassCard>
      </div>
    </div>
  );
}
